import { merge } from '../../../helper';

import appendChildren from '../../../general/append/appendChildren';

import * as exElement from '../../../utils/element';
import * as exResize from '../../table/resize';

// ----------------------------------------------------------------------------

function start(element: HTMLElement, config: Record<string, any> = {}): void {
    // Default Config
    const defConfig: Record<string, any> = {
        column: 0,
    };

    // Merge
    merge(defConfig, config);

    // ------------------------------------------------------------------------

    // Get elements (row)
    const listRow = element.querySelectorAll<HTMLTableRowElement>('tbody > tr');
    // Foreach List Row
    for (const elRow of listRow) {
        //
        const elements: Record<string, any> = {};

        // Get row index
        const rowIndex = elRow.dataset.ttId;
        // If there is no row index
        if (rowIndex == null) {
            continue;
        }

        // --------------------------------------------------------------------

        // Get element (cell)
        const elCell = elRow.cells[defConfig.column];

        // Create element (treetable column)
        const elColumn = exElement.create({
            class: {
                new: 'treetable-column',
            },
        });

        // Move all content (elements + text nodes) into treetable column
        appendChildren(elColumn, Array.from(elCell.childNodes));
        elCell.appendChild(elColumn);

        // --------------------------------------------------------------------

        // Get element (parent row)
        const elParentRow = element.querySelector<HTMLElement>('[data-tt-parent-id="' + rowIndex + '"]');
        // If there is element (parent row)
        if (elParentRow) {
            //
            elRow.removeClass('leaf').addClass('branch');

            // If using expandable
            if (defConfig.expandable === true) {
                // If there is no collapsed or expanded
                if (!elRow.hasClass('collapsed') || !elRow.hasClass('expanded')) {
                    // If using expanded
                    if (defConfig.initial === 'expanded') {
                        expand(element, rowIndex);
                    }
                    // Is default
                    else {
                        collapse(element, rowIndex);
                    }
                }
                // If there is collapsed or expanded
                else {
                    // If there is collapsed
                    if (elRow.hasClass('collapsed')) {
                        collapse(element, rowIndex);
                    }
                    // If there is expanded
                    else if (elRow.hasClass('expanded')) {
                        expand(element, rowIndex);
                    }
                }

                // ------------------------------------------------------------

                // If using icon
                if (defConfig.icon === true) {
                    // Create element (treetable folder)
                    const elFolder = exElement.create({
                        class: {
                            new: 'treetable-folder',
                        },
                        html: '<i class="fa-solid fa-folder"></i>',
                    });
                    elColumn.prependChild(elFolder);
                    elements.folder = elFolder;
                }

                // Create element (treetable open or close)
                const elToggle = exElement.create({
                    class: {
                        new: elRow.hasClass('expanded') ? 'treetable-open' : 'treetable-close',
                    },
                    html: '<i class="fa-solid fa-chevron-down"></i>',
                });
                elColumn.prependChild(elToggle);
                elements.toggle = elToggle;

                // ------------------------------------------------------------

                // Create event (click)
                elToggle.addEventListener('click', function (): any {
                    toggle(element, rowIndex);

                    // If there is collapsed
                    if (elRow.hasClass('collapsed')) {
                        elToggle.removeClass('treetable-open').addClass('treetable-close');

                        // If using icon
                        if (defConfig.icon === true) {
                            elements.folder.innerHTML = '<i class="fa-solid fa-folder">';
                        }
                    }
                    // If there is expanded
                    else if (elRow.hasClass('expanded')) {
                        elToggle.removeClass('treetable-close').addClass('treetable-open');

                        // If using icon
                        if (defConfig.icon === true) {
                            elements.folder.innerHTML = '<i class="fa-solid fa-folder-open">';
                        }
                    }
                });
            }
        }
        // If there is no element (parent)
        else {
            //
            elRow.removeClass('branch').addClass('leaf');

            // If using expandable
            if (defConfig.expandable === true) {
                // If using icon
                if (defConfig.icon === true) {
                    // Create element (treetable file)
                    const elFile = exElement.create({
                        class: {
                            new: 'treetable-file',
                        },
                        html: '<i class="fa-solid fa-file"></i>',
                    });
                    elColumn.prependChild(elFile);
                }

                // Create element (treetable none)
                const elNone = exElement.create({
                    class: {
                        new: 'treetable-none',
                    },
                });
                elColumn.prependChild(elNone);
            }
        }

        // --------------------------------------------------------------------

        // Get row length
        const rowLength = rowIndex.split('-').length;

        // Get row count
        const rowCount = rowLength > 1 ? rowLength - 1 : 0;

        // Foreach Row Count
        for (let i = 0; i < rowCount; i++) {
            // Create element (treetable none)
            const elNone = exElement.create({
                class: {
                    new: 'treetable-none',
                },
            });
            elColumn.prependChild(elNone);
        }
    }
}

// ----------------------------------------------------------------------------

function collapse(element: HTMLElement, id: string): void {
    // Get element (row)
    const elRow = element.querySelector<HTMLElement>('[data-tt-id="' + id + '"]');
    // If there is element (row)
    if (elRow && !elRow.hasClass('collapsed')) {
        elRow.removeClass('expanded').addClass('collapsed');

        // Function Callback
        const fnCallback = function (el: HTMLElement, rowId: string): void {
            // Get elements (row)
            const listRow = el.querySelectorAll<HTMLElement>('[data-tt-parent-id="' + rowId + '"]');
            // Foreach List Row
            for (const elChild of listRow) {
                // TODO
                fnCallback(el, elChild.dataset.ttId ?? '');
            }

            // Hide Element
            listRow.hideElement();
        };

        fnCallback(element, id);
        exResize.scroll(element);
    }
}

function expand(element: HTMLElement, id: string): void {
    // Get element (row)
    const elRow = element.querySelector<HTMLElement>('[data-tt-id="' + id + '"]');
    // If there is element (row)
    if (elRow && !elRow.hasClass('expanded')) {
        elRow.removeClass('collapsed').addClass('expanded');

        // Function Callback
        const fnCallback = function (el: HTMLElement, rowId: string): void {
            // Get elements (row)
            const listRow = el.querySelectorAll<HTMLElement>('[data-tt-parent-id="' + rowId + '"]');
            // Foreach List Row
            for (const elChild of listRow) {
                // If there is no collapsed
                if (!elChild.hasClass('collapsed')) {
                    // TODO
                    fnCallback(el, elChild.dataset.ttId ?? '');
                }
            }

            // Show Element
            listRow.showElement();
        };

        fnCallback(element, id);
        exResize.scroll(element);
    }
}

function toggle(element: HTMLElement, id: string): void {
    // Get element (row)
    const elRow = element.querySelector('[data-tt-id="' + id + '"]');
    // If there is element (row)
    if (elRow instanceof HTMLElement) {
        // If there is collapsed
        if (elRow.hasClass('collapsed')) {
            expand(element, id);
        }
        // If there is expanded
        else if (elRow.hasClass('expanded')) {
            collapse(element, id);
        }
    }
}

function remove(element: HTMLElement, id: string): void {
    // Get element (row)
    const elRow = element.querySelector('[data-tt-id="' + id + '"]');
    // If there is element (row)
    if (elRow instanceof HTMLElement) {
        // Get elements (row)
        const listRow = element.querySelectorAll<HTMLElement>('[data-tt-parent-id="' + id + '"]');
        // Foreach List Row
        for (const elRow2 of listRow) {
            // TODO
            remove(element, elRow2.dataset.ttId ?? '');
        }

        // Remove element
        elRow.remove();
    }
}

// ----------------------------------------------------------------------------

export { start, collapse, expand, toggle, remove };
